import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getVideosMuted,
  setVideosMuted,
  subscribeVideosMuted,
} from '../services/videoMute';
import { getJinglePlaying, subscribeJinglePlaying } from '../services/jinglePlayback';
import { getAudioUnlocked, subscribeAudioUnlocked } from '../services/audioUnlock';
import './GumletPlayer.scss';

const GUMLET_VIDEO_ID = '6a7dbe34c19d5bbff50c0801';
const GUMLET_ORIGIN = 'https://play.gumlet.io';

const liveIframes = new Map();

if (typeof window !== 'undefined' && !window.__gumletPlayerJsBound) {
  window.__gumletPlayerJsBound = true;
  window.addEventListener('message', (event) => {
    if (event.origin !== GUMLET_ORIGIN) return;
    let data = event.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (error) {
        return;
      }
    }
    if (!data || data.context !== 'player.js' || data.event !== 'ready') return;
    applyMuteToPlayers(getVideosMuted());
    playLivePlayers();
  });
  // The jingle (StudentMainPage) and any active video must never be audible
  // at the same time — re-evaluate mute state the instant the jingle starts
  // or stops so the currently active video mutes/unmutes in step with it.
  subscribeJinglePlaying(() => {
    applyMuteToPlayers(getVideosMuted());
  });
}

const postToIframe = (iframe, method, value) => {
  if (!iframe?.contentWindow) return;
  const message = {
    context: 'player.js',
    version: '3.0',
    method,
  };
  if (value !== undefined) message.value = value;
  iframe.contentWindow.postMessage(JSON.stringify(message), GUMLET_ORIGIN);
};

const applyMuteToPlayers = (muted) => {
  liveIframes.forEach(({ isActive, forceMuted }, iframe) => {
    const effectiveMuted = muted || forceMuted || getJinglePlaying() || !isActive();
    postToIframe(iframe, effectiveMuted ? 'mute' : 'unmute');
    if (!effectiveMuted) postToIframe(iframe, 'setVolume', 100);
  });
};

const playLivePlayers = () => {
  liveIframes.forEach(({ isActive }, iframe) => {
    if (isActive()) {
      applyMuteToPlayers(getVideosMuted());
      postToIframe(iframe, 'setLoop', true);
      postToIframe(iframe, 'play');
      return;
    }
    postToIframe(iframe, 'pause');
  });
};

export const kickFocusedPlayback = () => {
  playLivePlayers();
};

export const useVideosMuted = () => {
  const [muted, setMuted] = useState(getVideosMuted);

  useEffect(() => subscribeVideosMuted(setMuted), []);

  const toggleMuted = useCallback((event) => {
    event?.stopPropagation();
    event?.preventDefault();
    const nextMuted = !getVideosMuted();
    setVideosMuted(nextMuted);
    applyMuteToPlayers(nextMuted);
    if (!nextMuted) playLivePlayers();
  }, []);

  return [muted, toggleMuted];
};

const getPlayerSrc = (videoId, { interactive, loop }) => {
  const params = [
    'autoplay=true',
    `loop=${loop}`,
    'muted=true',
    `disable_player_controls=${!interactive}`,
  ].join('&');

  return `https://play.gumlet.io/embed/${videoId}?${params}`;
};

export const VideoMuteButton = ({ className = '' }) => {
  const [muted, toggleMuted] = useVideosMuted();
  const [audioUnlocked, setAudioUnlocked] = useState(getAudioUnlocked);

  useEffect(() => subscribeAudioUnlocked(setAudioUnlocked), []);

  // The button reflects whether sound is actually audible right now, not
  // just the user's mute preference — until a qualifying tap has happened,
  // the browser keeps everything silent regardless of that preference, and
  // showing "sound on" in that state is misleading.
  const displayMuted = muted || !audioUnlocked;

  return (
    <button
      type="button"
      className={`video-mute-button${className ? ` ${className}` : ''}`}
      onClick={toggleMuted}
      onPointerDown={(event) => event.stopPropagation()}
      aria-label={displayMuted ? 'בטל השתקה' : 'השתק'}
    >
      {displayMuted ? (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="currentColor"
            d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4 9.91 6.09 12 8.18V4z"
          />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="currentColor"
            d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
          />
        </svg>
      )}
    </button>
  );
};

const GumletPlayer = ({
  videoId = GUMLET_VIDEO_ID,
  title = 'Gumlet video player',
  isActive = true,
  shouldLoad = true,
  fill = false,
  interactive = false,
  loop = true,
  showMuteButton = false,
  forceMuted = false,
}) => {
  const iframeRef = useRef(null);
  const isActiveRef = useRef(isActive);
  const [muted] = useVideosMuted();

  isActiveRef.current = isActive;

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!shouldLoad || !iframe) return undefined;

    const isActiveNow = () => isActiveRef.current;
    liveIframes.set(iframe, { isActive: isActiveNow, forceMuted });

    const sync = () => {
      applyMuteToPlayers(getVideosMuted());
      if (isActiveRef.current) {
        postToIframe(iframe, 'setLoop', true);
        postToIframe(iframe, 'play');
        return;
      }
      postToIframe(iframe, 'pause');
    };

    iframe.addEventListener('load', sync);
    const retryA = window.setTimeout(sync, 400);
    const retryB = window.setTimeout(sync, 1200);
    const keepAlive = window.setInterval(() => {
      if (!isActiveRef.current) return;
      applyMuteToPlayers(getVideosMuted());
      postToIframe(iframe, 'play');
    }, 1500);

    return () => {
      iframe.removeEventListener('load', sync);
      window.clearTimeout(retryA);
      window.clearTimeout(retryB);
      window.clearInterval(keepAlive);
      postToIframe(iframe, 'pause');
      liveIframes.delete(iframe);
    };
  }, [shouldLoad, videoId, forceMuted]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    if (isActive) {
      applyMuteToPlayers(muted);
      postToIframe(iframe, 'play');
    } else {
      postToIframe(iframe, 'pause');
    }
  }, [isActive, muted]);

  const className = [
    'gumlet-player',
    fill ? 'gumlet-player--fill' : '',
    interactive ? 'gumlet-player--interactive' : '',
  ].filter(Boolean).join(' ');

  if (!shouldLoad) {
    return <div className={className} />;
  }

  return (
    <div className={className}>
      <iframe
        ref={iframeRef}
        title={title}
        src={getPlayerSrc(videoId, { interactive, loop })}
        referrerPolicy="origin"
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen; clipboard-write;"
      />
      {showMuteButton && <VideoMuteButton />}
    </div>
  );
};

export default GumletPlayer;
